import { Box, Divider, ListItemText, MenuItem, Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectConnectedCrmOrgs } from '../environmentsReducer';
import { Fragment, useCallback } from 'react';
import { Button, colors } from '@sweep-io/sweep-design';
import { StyledListItem } from '../../../common/StyledListItem';
import { CheckCircle } from '@sweep-io/sweep-design/dist/icons/CheckCircle';
import { Clock } from '@sweep-io/sweep-design/dist/icons/Clock';
import { Warning } from '@sweep-io/sweep-design/dist/icons/Warning';
import WithConnectOrgPermission from '../../../common/WithConnectOrgPermission';
import EnvFetchProgressBar from '../../../dashboard/fetch-progress-bar/EnvFetchProgressBar';
import { FetchStatus } from '@server/fetch_status.type';
import type { CrmOrg } from '../../../../types/CrmOrgs';
import { appRoutes } from '../../../../constants/appRoutes';
import { useNavigate } from 'react-router';
import { UserSettingsTab } from '../../../dashboard/user-settings/types';
import { PlatformIcon } from '../../../common/PlatformIcon';
import { useDefaultEnvId, useChangeDefaultEnv } from '../default-env-context/DefaultEnvContext';
import { getEnvironmentType } from '../EnvironmentTypeEnum';
import { OrgType } from '../../../../apis/facades/types/crmOrgsApiFacade.types';

interface EnvMenuItemsProps {
  onConnectProdClick: () => void;
  close: () => void;
  onChangeEnvCallback?: () => void;
  disableManageEnv?: boolean;
  allowedEnvIds?: string[];
  disableConnectProd?: boolean;
}

export const EnvMenuItems = ({
  onConnectProdClick,
  close,
  onChangeEnvCallback,
  disableManageEnv,
  allowedEnvIds,
  disableConnectProd,
}: EnvMenuItemsProps) => {
  const navigate = useNavigate();
  const defaultCreationCrmOrgId = useDefaultEnvId();
  const environments = useSelector(selectConnectedCrmOrgs);
  const changeEnvironment = useChangeDefaultEnv();

  const [prodEnvs, sandboxEnvs] = environments.reduce(
    (acc, curr) => {
      curr.isMain && curr.orgType === OrgType.Salesforce ? acc[0].push(curr) : acc[1].push(curr);
      return acc;
    },
    [[] as CrmOrg[], [] as CrmOrg[]],
  );
  sandboxEnvs.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const setDefaultCreationEnvironmentHandler = useCallback(
    (envId: string) => async () => {
      changeEnvironment(envId);
      close();
      onChangeEnvCallback && onChangeEnvCallback();
    },
    [changeEnvironment, close, onChangeEnvCallback],
  );

  const renderEnvItem = ({
    title,
    subtitle,
    onClick,
    EndAdornment,
    StartIcon,
    isActiveEnv,
    showDivider = true,
  }: {
    title: string;
    subtitle: string;
    onClick?: () => void;
    EndAdornment: React.ReactNode;
    StartIcon: React.ReactNode;
    isActiveEnv?: boolean;
    showDivider?: boolean;
  }) => {
    return (
      <>
        <StyledListItem
          StartIcon={StartIcon}
          title={title}
          subtitle={subtitle}
          onClick={onClick}
          sx={{
            cursor: onClick ? 'pointer' : 'default',
            maxHeight: '56px',
            mb: 0,
            p: '8px 24px 8px 16px',
            background: isActiveEnv ? colors.blue[100] : 'transparent',
            '&:hover': {
              background: isActiveEnv && onClick ? colors.blue[100] : undefined,
            },
            width: '304px',
            overflow: 'hidden',
            // Prevent stroke override for platform icons
            '& .platform-icon svg path': {
              stroke: 'unset',
            },
          }}
          EndAdornment={EndAdornment}
        />
        {showDivider && <Divider sx={{ borderColor: colors.grey[120] }} />}
      </>
    );
  };

  const connectProdItem = () => {
    if (salesforceProdEnvs?.length > 0) {
      return null;
    }
    return (
      <>
        {renderEnvItem({
          title: 'Production',
          subtitle: 'Not Connected',
          EndAdornment: (
            <WithConnectOrgPermission>
              <Button size="tiny" onClick={onConnectProdClick}>
                Connect
              </Button>
            </WithConnectOrgPermission>
          ),
          StartIcon: <Warning size="medium" />,
        })}
      </>
    );
  };
  const envs: CrmOrg[] = [...prodEnvs, ...sandboxEnvs].filter((env) =>
    allowedEnvIds ? allowedEnvIds.includes(env.id) : true,
  );
  const salesforceProdEnvs: CrmOrg[] = prodEnvs.filter((env) => env.orgType === OrgType.Salesforce);

  const renderItem = (env: CrmOrg, index: number, totalCount: number) => {
    const isActiveEnv = defaultCreationCrmOrgId === env.id;
    const envType = getEnvironmentType(env);

    const isError =
      env.fetchStatus === FetchStatus.Error || env.fetchStatus === FetchStatus.InvalidCredentials;
    const isFetching =
      env.fetchStatus === FetchStatus.Fetching || env.fetchStatus === FetchStatus.InitialFetching;

    const icons = {
      error: <Warning size="medium" />,
      fetching: <Clock size="medium" />,
      connected: <CheckCircle size="medium" />,
    };

    let icon = icons.connected;
    if (isError) {
      icon = icons.error;
    } else if (isFetching) {
      icon = icons.fetching;
    }

    const platformIcon = <PlatformIcon orgType={env.orgType} />;

    const isLastItem = index === totalCount - 1;
    const showDivider = !(isLastItem && disableManageEnv);

    return renderEnvItem({
      title: env.name,
      subtitle: envType,
      onClick: setDefaultCreationEnvironmentHandler(env.id),
      EndAdornment: (
        <Stack direction="row" alignItems="center" gap={1}>
          <EnvFetchProgressBar crmOrg={env} />
          <Box
            className="platform-icon"
            sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16 }}
          >
            {platformIcon}
          </Box>
        </Stack>
      ),
      StartIcon: icon,
      isActiveEnv,
      showDivider,
    });
  };

  return (
    <>
      {!disableConnectProd && connectProdItem()}
      {envs.map((env, index) => (
        <Fragment key={env.id}>{renderItem(env, index, envs.length)}</Fragment>
      ))}
      {!disableManageEnv && (
        <MenuItem
          onClick={() => {
            navigate(appRoutes.settings.route + `?tab=${UserSettingsTab.ENVIRONMENTS}`);
            close();
            return;
          }}
          sx={{
            p: '8px 24px 8px 16px',
            mt: 1,
          }}
        >
          <ListItemText sx={{ color: colors.blue[500] }}>Manage Environments</ListItemText>
        </MenuItem>
      )}
    </>
  );
};
